/**
 * useEditorMode Hook - 編輯模式管理
 *
 * 提供 Plain / WYSIWYG 雙模式切換功能
 *
 * @module editor/dual-mode/useEditorMode
 * @see Requirements 3.1, 3.2, 3.7
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { EditorMode } from '../types';
import { useEditorStore } from '../store/editorStore';
import { perfCollector } from '../perf/PerfCollector';

// ============================================================================
// Constants
// ============================================================================

/** 模式切換超時時間 (ms) - 超過此時間視為切換失敗 */
const MODE_SWITCH_TIMEOUT_MS = 100;

/** 模式切換鍵盤快捷鍵 */
const MODE_TOGGLE_SHORTCUT = {
  key: 'M',
  ctrlKey: true,
  shiftKey: true,
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * useEditorMode Hook 回傳介面
 */
export interface UseEditorModeReturn {
  /** 當前編輯模式 */
  mode: EditorMode;
  /** 設定編輯模式 */
  setMode: (mode: EditorMode) => void;
  /** 切換編輯模式 */
  toggleMode: () => void;
  /** 是否正在切換模式 */
  isTransitioning: boolean;
}

/**
 * useEditorMode Hook 選項
 */
export interface UseEditorModeOptions {
  /** 初始模式 (預設: 'plain') */
  initialMode?: EditorMode;
  /** 是否啟用鍵盤快捷鍵 (預設: true) */
  enableKeyboardShortcut?: boolean;
  /** 模式切換回調 */
  onModeChange?: (mode: EditorMode) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * 編輯模式管理 Hook
 *
 * 提供 Plain / WYSIWYG 雙模式切換功能，包含：
 * - mode 狀態管理 (與 editorStore 同步)
 * - setMode / toggleMode 方法
 * - isTransitioning 狀態追蹤
 * - Ctrl+Shift+M 鍵盤快捷鍵支援
 *
 * @param options - Hook 選項
 * @returns UseEditorModeReturn
 *
 * @example
 * ```tsx
 * const { mode, setMode, toggleMode, isTransitioning } = useEditorMode();
 *
 * // 切換模式
 * toggleMode();
 *
 * // 設定特定模式
 * setMode('wysiwyg');
 * ```
 */
export function useEditorMode(options: UseEditorModeOptions = {}): UseEditorModeReturn {
  const { initialMode = 'plain', enableKeyboardShortcut = true, onModeChange } = options;

  // 從 store 取得 mode 狀態和 setMode action
  const storeMode = useEditorStore((state) => state.mode);
  const storeSetMode = useEditorStore((state) => state.setMode);

  // 本地 transitioning 狀態
  const [isTransitioning, setIsTransitioning] = useState(false);

  // 追蹤是否已初始化
  const isInitialized = useRef(false);

  // 切換超時計時器
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // 初始化模式
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isInitialized.current) {
      storeSetMode(initialMode);
      isInitialized.current = true;
    }
  }, [initialMode, storeSetMode]);

  // ─────────────────────────────────────────────────────────────────────────
  // 清理計時器
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // setMode - 設定編輯模式
  // ─────────────────────────────────────────────────────────────────────────

  const setMode = useCallback(
    (newMode: EditorMode) => {
      // 如果模式相同，不做任何事
      if (newMode === storeMode) {
        return;
      }

      // 開始計時
      const startTime = performance.now();

      // 設定 transitioning 狀態
      setIsTransitioning(true);

      // 清除之前的超時計時器
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // 更新 store 中的模式
      storeSetMode(newMode);

      // 設定超時計時器，確保 transitioning 狀態會被重置
      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);

        // 記錄效能樣本
        const duration = performance.now() - startTime;
        perfCollector.sample('modeSwitch', duration, {
          from: storeMode,
          to: newMode,
        });

        // 觸發回調
        onModeChange?.(newMode);
      }, MODE_SWITCH_TIMEOUT_MS);
    },
    [storeMode, storeSetMode, onModeChange]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // toggleMode - 切換編輯模式
  // ─────────────────────────────────────────────────────────────────────────

  const toggleMode = useCallback(() => {
    const newMode: EditorMode = storeMode === 'plain' ? 'wysiwyg' : 'plain';
    setMode(newMode);
  }, [storeMode, setMode]);

  // ─────────────────────────────────────────────────────────────────────────
  // 鍵盤快捷鍵處理 (Ctrl+Shift+M)
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!enableKeyboardShortcut) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // 檢查是否為 Ctrl+Shift+M (或 Cmd+Shift+M on Mac)
      const isModKey = event.ctrlKey || event.metaKey;
      const isShiftKey = event.shiftKey;
      const isTargetKey = event.key.toUpperCase() === MODE_TOGGLE_SHORTCUT.key;

      if (isModKey && isShiftKey && isTargetKey) {
        event.preventDefault();
        toggleMode();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableKeyboardShortcut, toggleMode]);

  // ─────────────────────────────────────────────────────────────────────────
  // 回傳值
  // ─────────────────────────────────────────────────────────────────────────

  return {
    mode: storeMode,
    setMode,
    toggleMode,
    isTransitioning,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * 檢查是否為有效的編輯模式
 */
export function isValidEditorMode(mode: unknown): mode is EditorMode {
  return mode === 'plain' || mode === 'wysiwyg';
}

/**
 * 取得模式的顯示名稱
 */
export function getModeName(mode: EditorMode): string {
  return mode === 'plain' ? 'Plain Text' : 'WYSIWYG';
}

/**
 * 取得模式切換的快捷鍵描述
 */
export function getModeToggleShortcutLabel(): string {
  // 檢測是否為 Mac
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return isMac ? '⌘⇧M' : 'Ctrl+Shift+M';
}
